/**
 * KaTeX Renderer Utility
 * Provides utilities for rendering LaTeX/KaTeX formulas in Vue components
 * Supports both inline ($...$) and display ($$...$$) math formulas
 */

import katex from "katex";
import "katex/dist/katex.min.css";
import DOMPurify from "dompurify";

/**
 * Render LaTeX formulas in text
 * Supports:
 * - Inline math: $formula$ or \(...\)
 * - Display math: $$formula$$ or \[...\]
 *
 * @param text - Text containing LaTeX formulas
 * @param options - KaTeX rendering options
 * @returns HTML string with rendered formulas
 */
export function renderLatex(
  text: string,
  options: {
    displayMode?: boolean;
    throwOnError?: boolean;
    errorColor?: string;
    macros?: Record<string, string>;
  } = {}
): string {
  if (!text || typeof text !== "string") {
    return "";
  }

  const defaultOptions = {
    throwOnError: false,
    errorColor: "#cc0000",
    macros: {
      "\\R": "\\mathbb{R}",
      "\\N": "\\mathbb{N}",
      "\\Z": "\\mathbb{Z}",
      "\\Q": "\\mathbb{Q}",
    },
    ...options,
  };

  try {
    // Step 1: Process display math ($$...$$) and \[...\]
    let processed = text.replace(/\$\$([\s\S]+?)\$\$/g, (match, formula) => {
      try {
        const rendered = katex.renderToString(formula.trim(), {
          ...defaultOptions,
          displayMode: true,
        });
        return `<span class="katex-display-wrapper">${rendered}</span>`;
      } catch (error) {
        console.warn("KaTeX display math rendering error:", error);
        return `<span class="katex-error" style="color: ${defaultOptions.errorColor};">${match}</span>`;
      }
    });

    processed = processed.replace(/\\\[([\s\S]+?)\\\]/g, (match, formula) => {
      try {
        const rendered = katex.renderToString(formula.trim(), {
          ...defaultOptions,
          displayMode: true,
        });
        return `<span class="katex-display-wrapper">${rendered}</span>`;
      } catch (error) {
        console.warn("KaTeX display math rendering error:", error);
        return `<span class="katex-error" style="color: ${defaultOptions.errorColor};">${match}</span>`;
      }
    });

    // Step 2: Process inline math ($...$) and \(...\)
    // Be careful not to match escaped dollar signs (\$)
    processed = processed.replace(
      /(?<!\\)\$(?!\$)((?:\\.|[^$\\])+?)(?<!\\)\$/g,
      (match, formula) => {
        try {
          const rendered = katex.renderToString(formula.trim(), {
            ...defaultOptions,
            displayMode: false,
          });
          return `<span class="katex-inline-wrapper">${rendered}</span>`;
        } catch (error) {
          console.warn("KaTeX inline math rendering error:", error);
          return `<span class="katex-error" style="color: ${defaultOptions.errorColor};">${match}</span>`;
        }
      }
    );

    processed = processed.replace(/\\\(([\s\S]+?)\\\)/g, (match, formula) => {
      try {
        const rendered = katex.renderToString(formula.trim(), {
          ...defaultOptions,
          displayMode: false,
        });
        return `<span class="katex-inline-wrapper">${rendered}</span>`;
      } catch (error) {
        console.warn("KaTeX inline math rendering error:", error);
        return `<span class="katex-error" style="color: ${defaultOptions.errorColor};">${match}</span>`;
      }
    });

    // Step 3: Sanitize HTML to prevent XSS while preserving KaTeX output
    return DOMPurify.sanitize(processed, {
      ADD_TAGS: [
        "math",
        "semantics",
        "mrow",
        "mi",
        "mo",
        "mn",
        "msup",
        "msub",
        "mfrac",
        "munderover",
        "annotation",
      ],
      ADD_ATTR: ["class", "style", "xmlns"],
      ALLOW_DATA_ATTR: true,
    });
  } catch (error) {
    console.error("KaTeX rendering error:", error);
    return text; // Return original text if rendering fails
  }
}

/**
 * Check if text contains LaTeX formulas
 * @param text - Text to check
 * @returns true if text contains LaTeX formulas
 */
export function containsLatex(text: string): boolean {
  if (!text || typeof text !== "string") {
    return false;
  }

  // Check for common LaTeX delimiters
  const latexPatterns = [
    /\$\$[\s\S]+?\$\$/, // Display math: $$...$$
    /(?<!\\)\$(?!\$)[\s\S]+?(?<!\\)\$/, // Inline math: $...$
    /\\\[[\s\S]+?\\\]/, // Display math: \[...\]
    /\\\([\s\S]+?\\\)/, // Inline math: \(...\)
  ];

  return latexPatterns.some((pattern) => pattern.test(text));
}

/**
 * Render a single LaTeX formula
 * @param formula - LaTeX formula string
 * @param displayMode - Whether to render in display mode (centered, larger)
 * @returns HTML string with rendered formula
 */
export function renderFormula(formula: string, displayMode: boolean = false): string {
  if (!formula || typeof formula !== "string") {
    return "";
  }

  try {
    const rendered = katex.renderToString(formula.trim(), {
      displayMode,
      throwOnError: false,
      errorColor: "#cc0000",
    });

    return DOMPurify.sanitize(rendered, {
      ADD_TAGS: [
        "math",
        "semantics",
        "mrow",
        "mi",
        "mo",
        "mn",
        "msup",
        "msub",
        "mfrac",
        "munderover",
        "annotation",
      ],
      ADD_ATTR: ["class", "style", "xmlns"],
      ALLOW_DATA_ATTR: true,
    });
  } catch (error) {
    console.error("KaTeX formula rendering error:", error);
    return `<span class="katex-error" style="color: #cc0000;">${formula}</span>`;
  }
}

/**
 * Extract LaTeX formulas from text
 * @param text - Text containing LaTeX formulas
 * @returns Array of formula objects with type and content
 */
export function extractFormulas(
  text: string
): Array<{ type: "inline" | "display"; content: string; start: number; end: number }> {
  if (!text || typeof text !== "string") {
    return [];
  }

  const formulas: Array<{
    type: "inline" | "display";
    content: string;
    start: number;
    end: number;
  }> = [];

  // Extract display math ($$...$$)
  const displayPattern = /\$\$([\s\S]+?)\$\$/g;
  let match;
  while ((match = displayPattern.exec(text)) !== null) {
    formulas.push({
      type: "display",
      content: match[1].trim(),
      start: match.index,
      end: match.index + match[0].length,
    });
  }

  // Extract inline math ($...$)
  const inlinePattern = /(?<!\\)\$(?!\$)((?:\\.|[^$\\])+?)(?<!\\)\$/g;
  while ((match = inlinePattern.exec(text)) !== null) {
    formulas.push({
      type: "inline",
      content: match[1].trim(),
      start: match.index,
      end: match.index + match[0].length,
    });
  }

  // Sort by start position
  return formulas.sort((a, b) => a.start - b.start);
}
